import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { MapPin, Trash2, Plus, User, Bell, LifeBuoy, ChevronRight } from "lucide-react";

const CITY_COORDS = { Istanbul: { lat: 41.02, lng: 28.98 }, Ankara: { lat: 39.93, lng: 32.86 }, Izmir: { lat: 38.42, lng: 27.14 } };
const EVENT_ORDER = ["confirmed", "preparing", "courier_assigned", "arriving", "arriving_soon", "picked_up", "on_the_way", "courier_arrived", "delivered", "cancelled", "payment_failed", "refund"];

export default function Profile() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [addrs, setAddrs] = useState([]);
  const [form, setForm] = useState({ label: "Home", line1: "", city: "Istanbul", ...CITY_COORDS.Istanbul });

  const load = () => api.get("/me/addresses").then((r) => setAddrs(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.line1) { toast.error(t("prefs.enterLine")); return; }
    await api.post("/me/addresses", form);
    setForm({ label: "Home", line1: "", city: "Istanbul", ...CITY_COORDS.Istanbul });
    toast.success(t("prefs.addressSaved"));
    load();
  };
  const del = async (id) => { await api.delete(`/me/addresses/${id}`); load(); };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-display font-bold">{t("prefs.profile")}</h1>

      <div className="rounded-2xl border bg-card p-5 flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-brand/15 text-brand flex items-center justify-center"><User className="w-6 h-6" /></div>
        <div><div className="font-display font-bold text-lg">{user?.name}</div><div className="text-sm text-muted-foreground">{user?.email}</div></div>
      </div>

      <NotificationPrefs />

      <div className="rounded-2xl border bg-card p-5 space-y-4">
        <div className="flex items-center gap-2 font-semibold"><MapPin className="w-4 h-4 text-brand" /> {t("prefs.savedAddresses")}</div>
        {addrs.map((a) => (
          <div key={a.id} data-testid={`saved-addr-${a.id}`} className="flex items-center justify-between rounded-xl border p-3">
            <div><div className="font-medium text-sm">{a.label} · {a.city}</div><div className="text-xs text-muted-foreground">{a.line1}</div></div>
            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(a.id)}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        <div className="grid sm:grid-cols-2 gap-3 pt-2">
          <div><Label>{t("prefs.label")}</Label><Input data-testid="p-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1 rounded-xl" /></div>
          <div><Label>{t("prefs.city")}</Label>
            <select data-testid="p-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value, ...CITY_COORDS[e.target.value] })} className="mt-1 w-full h-10 px-3 rounded-xl border bg-card text-sm">
              {Object.keys(CITY_COORDS).map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2"><Label>{t("prefs.addressLine")}</Label><Input data-testid="p-line" value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} className="mt-1 rounded-xl" /></div>
        </div>
        <Button data-testid="add-addr" onClick={add} className="rounded-full bg-brand hover:bg-brand-dark gap-2"><Plus className="w-4 h-4" /> {t("prefs.addAddress")}</Button>
      </div>

      <Link to="/support" data-testid="profile-support-link" className="flex items-center justify-between rounded-2xl border bg-card p-5 hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-2 font-semibold"><LifeBuoy className="w-4 h-4 text-brand" /> {t("nav.support")}</div>
        <ChevronRight className="w-4 h-4 text-muted-foreground rtl:rotate-180" />
      </Link>
    </div>
  );
}

function NotificationPrefs() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me/notification-preferences")
      .then((r) => setPrefs(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (ev) => setPrefs((p) => ({ ...p, events: { ...p.events, [ev]: !p.events[ev] } }));
  const setLang = (v) => setPrefs((p) => ({ ...p, language: v || null }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/notification-preferences", { events: prefs.events, language: prefs.language || "" });
      setPrefs(data);
      toast.success(t("prefs.saved"));
    } catch { toast.error(t("prefs.saveFail")); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="notif-prefs-section" className="rounded-2xl border bg-card p-5 space-y-4">
      <div>
        <div className="flex items-center gap-2 font-semibold"><Bell className="w-4 h-4 text-brand" /> {t("prefs.title")}</div>
        <div className="text-sm text-muted-foreground mt-1">{t("prefs.subtitle")}</div>
      </div>

      {loading ? <div data-testid="prefs-loading" className="text-sm text-muted-foreground py-6 text-center">…</div>
        : error ? <div data-testid="prefs-error" className="text-sm text-red-600 py-6 text-center">{t("prefs.loadFail")}</div>
        : prefs && (
          <>
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("prefs.email")}</div>
              {(prefs.available_events || EVENT_ORDER).sort((a, b) => EVENT_ORDER.indexOf(a) - EVENT_ORDER.indexOf(b)).map((ev) => (
                <div key={ev} className="flex items-center justify-between py-2 border-b last:border-0">
                  <span className="text-sm">{t(`admin.notifEvent.${ev}`, ev)}</span>
                  <Switch data-testid={`pref-toggle-${ev}`} checked={!!prefs.events[ev]} onCheckedChange={() => toggle(ev)} />
                </div>
              ))}
            </div>

            <div>
              <Label className="mb-1 block">{t("prefs.notifLang")}</Label>
              <select data-testid="pref-lang-select" value={prefs.language || ""} onChange={(e) => setLang(e.target.value)} className="w-full h-10 px-3 rounded-xl border bg-card text-sm">
                <option value="">{t("prefs.followApp")}</option>
                <option value="en">{t("prefs.langEn")}</option>
                <option value="tr">{t("prefs.langTr")}</option>
                <option value="he">{t("prefs.langHe")}</option>
                <option value="ar">{t("prefs.langAr")}</option>
              </select>
            </div>

            <Button data-testid="save-prefs-btn" onClick={save} disabled={saving} className="rounded-full bg-brand hover:bg-brand-dark">{t("prefs.save")}</Button>
          </>
        )}
    </div>
  );
}
